import type { CSSProperties, SVGProps } from 'react';

import { cn } from '~/lib/utils';

// Matches the rendered size of the previous Lottie loader at each preset.
const loaderSize = {
  small: 'size-4',
  medium: 'size-7.5',
  large: 'size-20',
} as const;

// Logomark spokes in sweep order, from the lower end of the "C" clockwise to the upper end.
// origin: outer end of the spoke, which it grows inward from. angle: spoke axis.
// dot: length scale at which the spoke is as long as it is wide. delay: position in the sweep.
// Animation lives in index.css (.conduit-loader-spoke).
const SPOKES = [
  {
    d: 'M372.707 410.738C372.426 410.072 371.716 409.697 371.005 409.828C367.317 410.511 363.566 410.956 359.775 411.15C358.706 411.205 357.998 412.302 358.413 413.288L365.604 430.385C365.93 431.159 366.821 431.523 367.595 431.197L377.849 426.885C378.623 426.559 378.986 425.668 378.661 424.894L372.707 410.738Z',
    origin: '372.72px 429.04px',
    angle: '67.20deg',
    dot: 0.656,
    delay: '0.000s',
  },
  {
    d: 'M325.531 407.022C325.531 406.384 325.131 405.815 324.535 405.588C320.762 404.157 317.139 402.483 313.679 400.587C312.647 400.022 311.367 400.757 311.367 401.933V438.47C311.367 439.31 312.047 439.99 312.887 439.99H324.011C324.85 439.99 325.531 439.31 325.531 438.47V407.022Z',
    origin: '318.45px 439.99px',
    angle: '90.00deg',
    dot: 0.358,
    delay: '0.064s',
  },
  {
    d: 'M290.889 383.796C291.118 383.252 291.01 382.624 290.618 382.182C287.846 379.056 285.309 375.738 283.023 372.258C282.342 371.222 280.761 371.344 280.28 372.487L258.237 424.894C257.911 425.668 258.275 426.559 259.049 426.885L269.302 431.197C270.076 431.523 270.968 431.159 271.293 430.385L290.889 383.796Z',
    origin: '264.18px 429.04px',
    angle: '112.80deg',
    dot: 0.237,
    delay: '0.129s',
  },
  {
    d: 'M273.357 355.107C273.775 354.688 273.912 354.065 273.714 353.507C272.33 349.594 271.223 345.572 270.41 341.471C270.17 340.255 268.661 339.772 267.784 340.648L215.528 392.905C214.934 393.499 214.934 394.461 215.528 395.055L223.393 402.921C223.987 403.514 224.949 403.514 225.543 402.921L273.357 355.107Z',
    origin: '219.46px 398.99px',
    angle: '135.00deg',
    dot: 0.184,
    delay: '0.192s',
  },
  {
    d: 'M267.878 328.954C268.465 328.707 268.834 328.122 268.814 327.486C268.689 323.529 268.837 319.531 269.271 315.517C269.398 314.345 268.222 313.443 267.135 313.901L188.063 347.159C187.289 347.484 186.926 348.376 187.251 349.15L191.564 359.403C191.89 360.177 192.781 360.541 193.555 360.215L267.878 328.954Z',
    origin: '189.40px 354.27px',
    angle: '157.20deg',
    dot: 0.16,
    delay: '0.254s',
  },
  {
    d: 'M269.371 307.083C270.091 307.083 270.711 306.578 270.868 305.876C271.133 304.691 271.424 303.507 271.741 302.324C272.41 299.829 273.181 297.388 274.049 295.006C274.416 293.999 273.679 292.919 272.607 292.919H179.978C179.139 292.919 178.458 293.599 178.458 294.439V305.563C178.458 306.402 179.139 307.083 179.978 307.083H269.371Z',
    origin: '178.46px 300.00px',
    angle: '-180.00deg',
    dot: 0.148,
    delay: '0.319s',
  },
  {
    d: 'M274.725 289.293C275.491 289.615 276.372 289.262 276.719 288.507C278.253 285.167 279.986 281.958 281.897 278.893C282.4 278.088 282.073 277.017 281.198 276.649L193.556 239.786C192.782 239.461 191.891 239.824 191.565 240.598L187.253 250.852C186.927 251.626 187.291 252.517 188.065 252.843L274.725 289.293Z',
    origin: '189.40px 245.73px',
    angle: '-157.20deg',
    dot: 0.145,
    delay: '0.383s',
  },
  {
    d: 'M282.755 274.325C283.427 274.997 284.543 274.894 285.098 274.124C287.24 271.153 289.558 268.337 292.034 265.687C292.601 265.081 292.594 264.133 292.007 263.546L225.542 197.081C224.949 196.487 223.986 196.487 223.392 197.081L215.527 204.947C214.933 205.54 214.933 206.503 215.527 207.097L282.755 274.325Z',
    origin: '219.46px 201.01px',
    angle: '-135.00deg',
    dot: 0.144,
    delay: '0.446s',
  },
  {
    d: 'M294.18 260.574C294.595 261.56 295.872 261.822 296.659 261.097C299.441 258.537 302.383 256.166 305.462 253.998C306.053 253.582 306.281 252.812 306.001 252.146L271.288 169.616C270.963 168.842 270.071 168.478 269.297 168.804L259.044 173.116C258.27 173.442 257.906 174.333 258.232 175.107L294.18 260.574Z',
    origin: '264.18px 170.96px',
    angle: '-112.80deg',
    dot: 0.148,
    delay: '0.508s',
  },
  {
    d: 'M325.531 161.53C325.531 160.69 324.85 160.01 324.011 160.01H312.887C312.047 160.01 311.367 160.69 311.367 161.53V247.526C311.367 248.703 312.648 249.437 313.679 248.871C317.175 246.952 320.803 245.272 324.537 243.847C325.133 243.62 325.531 243.051 325.531 242.414V161.53Z',
    origin: '318.45px 160.01px',
    angle: '-90.00deg',
    dot: 0.159,
    delay: '0.573s',
  },
  {
    d: 'M336.991 237.655C336.51 238.799 337.53 240.014 338.747 239.776C342.812 238.979 346.955 238.473 351.146 238.271C351.737 238.243 352.262 237.881 352.492 237.336L378.666 175.107C378.991 174.333 378.628 173.442 377.854 173.116L367.6 168.804C366.826 168.478 365.935 168.842 365.609 169.616L336.991 237.655Z',
    origin: '372.72px 170.96px',
    angle: '-67.20deg',
    dot: 0.184,
    delay: '0.637s',
  },
  {
    d: 'M371.233 237.2C370.357 238.076 370.839 239.584 372.055 239.825C373.94 240.199 375.827 240.637 377.711 241.142C379.885 241.725 382.017 242.385 384.106 243.119C384.663 243.316 385.285 243.179 385.703 242.761L421.369 207.094C421.963 206.501 421.963 205.538 421.369 204.944L413.504 197.079C412.91 196.485 411.947 196.485 411.354 197.079L371.233 237.2Z',
    origin: '417.44px 201.01px',
    angle: '-45.00deg',
    dot: 0.237,
    delay: '0.700s',
  },
];

// The 280-unit-tall mark sits in a 336-unit box: the same 20/24 inset as a Lucide icon, so it
// lines up with the icons it swaps in for.
const VIEW_BOX = '132 132 336 336';

type Props = SVGProps<SVGSVGElement> & {
  size?: keyof typeof loaderSize;
};

export default function ConduitHealthLoader({ size = 'medium', className, ...props }: Props) {
  const decorative = props['aria-hidden'] === true || props['aria-hidden'] === 'true';

  return (
    <svg
      viewBox={VIEW_BOX}
      fill="currentColor"
      role={decorative ? undefined : 'status'}
      aria-label={decorative ? undefined : 'Loading'}
      className={cn('shrink-0', loaderSize[size], className)}
      {...props}
    >
      {SPOKES.map((spoke) => (
        <path
          key={spoke.delay}
          d={spoke.d}
          className="conduit-loader-spoke"
          style={
            {
              '--origin': spoke.origin,
              '--angle': spoke.angle,
              '--dot': spoke.dot,
              '--delay': spoke.delay,
            } as CSSProperties
          }
        />
      ))}
    </svg>
  );
}
